import React, { useState, useEffect } from 'react';
import { useSearchParams } from "../../../../backend/navigation";
import {
  MFG_STATUS,
  DROPDOWN,
  TITLES,
  ROUTES,
  POPUP_TYPE,
  DATA_FIELD,
  BUTTON,
  MANUFACTURING_ESUN_LIST_COLUMN,
} from '../../../../constants';
import { DropDown, PaginatedTable, SearchBar, TitleBar } from '../../../common';
import { StatusBox } from './StatusBox';
import { GreenTile, RedTile, YellowTile } from './Tiles';
import { vendorLot } from '../../../../service';
import { useDebounce } from '../../../../hooks';
import { popup, manufacturingDataCompiler, reducedCalc } from '../../../../utils';
import { Button } from '../../../shared';

export const ManufacturingContent = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params or default values
  const [mfgData, setMfgData] = useState([]);
  const [sort1, setSort1] = useState(searchParams.get('priority') || 'Priority');
  const [sort2, setSort2] = useState(searchParams.get('qtyStatus') || 'Qty Status');
  const [sort3, setSort3] = useState(searchParams.get('readyStatus') || 'Ready Status');
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [search, setSearch] = useState(false);
  const [sortingArray1, setSortingArray1] = useState([]);
  const [sortingArray2, setSortingArray2] = useState([]);
  const [sortingArray3] = useState(['Not Ready', 'Partially Ready', 'Fully Ready']);
  const [sortedData, setSortedData] = useState([]);
  const [paginationData, setPaginationData] = useState({});
  const [pageNumber, setPageNumber] = useState(parseInt(searchParams.get('page')) || 1);
  const [jumpPage, setJumpPage] = useState('');
  const [listView, setListView] = useState(searchParams.get('listView') === 'true' || false);
  const [isLoading, setIsLoading] = useState(false);
  const [query, setQuery] = useState(() => ({ term: searchTerm, priority: sort1, qtyStatus: sort2, readyStatus: sort3, page: pageNumber }));

  // Update URL params when values change
  const updateUrlParams = (params = {}) => {
    const newParams = {};

    // Only include non-empty values
    if (params.search) newParams.search = params.search;
    // Only include filters if they're not the default values
    if (params.priority && params.priority !== 'Priority') newParams.priority = params.priority;
    if (params.qtyStatus && params.qtyStatus !== 'Qty Status')
      newParams.qtyStatus = params.qtyStatus;
    if (params.readyStatus && params.readyStatus !== 'Ready Status')
      newParams.readyStatus = params.readyStatus;
    if (params.page) newParams.page = params.page.toString();
    if (params.listView !== undefined) newParams.listView = params.listView.toString();
    setSearchParams(newParams, { replace: true });
  };

  const getManufacturingList = useDebounce(({ term, priority, qtyStatus, readyStatus, page }) => {
    setIsLoading(true);
    const params = {
      page_number: page,
      search: term
        ? {
          future_lot: term,
          'product_id.default_code': term,
          'dyeing_product_id.default_code': term,
        }
        : {},
      sort: {
        selection: {
          ...(priority !== 'Priority'
            ? { manufacture_priority: priority?.toString().toLowerCase().replaceAll(' ', '_') }
            : {}),
          ...(qtyStatus !== 'Qty Status'
            ? {
              qty_status: qtyStatus?.toString().toLowerCase().replaceAll(' ', '_'),
            }
            : {}),
          ...(readyStatus !== 'Ready Status'
            ? {
              ready_status: readyStatus?.toString().toLowerCase().replaceAll(' ', '_'),
            }
            : {}),
        },
      },
    };

    // Update URL params
    updateUrlParams({
      search: term,
      priority: priority,
      qtyStatus: qtyStatus,
      readyStatus: readyStatus,
      page: params.page_number,
      listView: listView,
    });


    vendorLot({ data: params })
      .then((response) => {
        setIsLoading(false);
        const records = response?.data?.records || [];
        if (records.length > 0) {
          const compiledData = manufacturingDataCompiler(records);
          setMfgData(compiledData);
          setPaginationData(response.data.params);

          // Always update the sorting arrays with the latest data from the API
          if (response?.data?.manufacture_priority) {
            setSortingArray1(response.data.manufacture_priority);
          }

          if (response?.data?.qty_status) {
            setSortingArray2([
              ...response.data.qty_status.filter((item) => !DROPDOWN.MFG_SORT_2.includes(item)),
            ]);
          }
        } else {
          setMfgData([]);
        }
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, 400);

  useEffect(() => {
    getManufacturingList(query);
  }, [getManufacturingList, query]);

  const submitQuery = ({ term = searchTerm.trim(), priority = sort1, qtyStatus = sort2, readyStatus = sort3, page = 1 } = {}) => {
    setPageNumber(page);
    setQuery({ term, priority, qtyStatus, readyStatus, page });
  };
  const handleSearch = (value) => {
    setSearch(value);
    if (value) submitQuery();
  };
  const handlePageChange = (value) => {
    const page = typeof value === 'function' ? value(pageNumber) : value;
    if (page && page !== pageNumber) submitQuery({ page });
  };
  const handleSort = (field, value) => {
    ({ priority: setSort1, qtyStatus: setSort2, readyStatus: setSort3 })[field](value);
    submitQuery({ [field]: value });
  };
  const handleListView = (value) => {
    const next = typeof value === 'function' ? value(listView) : value;
    setListView(next);
    updateUrlParams({ search: searchTerm, priority: sort1, qtyStatus: sort2, readyStatus: sort3, page: pageNumber, listView: next });
  };
  const clearSearch = () => {
    setSearch(false);
    submitQuery({ term: '' });
  };
  useEffect(() => {
    setSortedData(mfgData);
  }, [mfgData]);
  const resetFilter = () => {
    setSort1('Priority');
    setSort2('Qty Status');
    setSort3('Ready Status');
    submitQuery({ priority: 'Priority', qtyStatus: 'Qty Status', readyStatus: 'Ready Status' });
  };

  const handleJumpToPage = () => {
    const totalPages = Math.ceil(paginationData.total_records / 20);
    const targetPage = parseInt(jumpPage, 10);

    if (targetPage >= 1 && targetPage <= totalPages) {
      handlePageChange(targetPage);
    } else {
      popup(POPUP_TYPE.ERR, `Please enter a valid page number between 1 and ${totalPages}.`);
    }
  };

  return (
    <>
      <TitleBar
        title={[{ text: TITLES.MANUFACTURING, route: ROUTES.MANUFACTURING }]}
        listView={listView}
        setListView={handleListView}
      />
      {!listView && <StatusBox status={MFG_STATUS} />}
      <div className="d-flex flex-wrap align-items-start justify-content-start gap-3 w-100">
        <div className="flex-grow-2">
          <SearchBar
            search={search}
            setSearchTerm={setSearchTerm}
            searchTerm={searchTerm}
            setSearch={handleSearch}
            onDefaultSearch={clearSearch}
          />
        </div>
        <div className=" d-flex flex-wrap align-items-center justify-content-start gap-2 mb-3">
          <DropDown dropdownMenu={sortingArray1} value={sort1} setValue={(value) => handleSort('priority', value)} />
          <DropDown dropdownMenu={sortingArray2} value={sort2} setValue={(value) => handleSort('qtyStatus', value)} />
          <DropDown dropdownMenu={sortingArray3} value={sort3} setValue={(value) => handleSort('readyStatus', value)} />
          {(sort1 !== 'Priority' || sort2 !== 'Qty Status' || sort3 !== 'Ready Status') && (
            <Button
              clickHandler={resetFilter}
              className="blue-btn d-flex align-items-center justify-content-center px-3">
              {BUTTON.CLEAR_FILTERS}
            </Button>
          )}
        </div>
      </div>

      {!listView && mfgData.length > 0 && (
        <div className="pagination-container d-flex justify-content-between align-items-center mb-2">
          <h5 className="pagination-info">
            <span className="highlight">Records:</span>
            <span className="record-range">
              {paginationData.start} - {paginationData.end}
            </span>
            <span className="highlight">of</span>
            <span className="total-records">{paginationData.total_records}</span>
          </h5>

          <div className="pagination-controls d-flex align-items-center justify-content-end">
            <Button
              className={`blue-btn d-flex align-items-center justify-content-center px-3 me-2 ${paginationData.page === 1 ? 'disabled' : ''
                }`}
              clickHandler={() => handlePageChange((prev) => Math.max(prev - 1, 1))}
              disabled={paginationData.page === 1}>
              Previous
            </Button>
            <div className="page-jump-container d-flex align-items-center my-2">
              <input
                type="number"
                className="page-jump-input form-control me-2"
                placeholder={`On page ${paginationData.page} of ${Math.ceil(paginationData.total_records / 20)}`}
                value={jumpPage}
                disabled={Math.ceil(paginationData.total_records / 20) === 1}
                readOnly={Math.ceil(paginationData.total_records / 20) === 1}
                onWheel={(e) => e.target.blur()}
                onChange={(e) => {
                  const value = e.target.value;
                  if (
                    value === '' ||
                    (/^[0-9]+$/.test(value) &&
                      value > 0 &&
                      value <= Math.ceil(paginationData.total_records / 20))
                  ) {
                    setJumpPage(value);
                  } else {
                    setJumpPage('');
                  }
                }}
              />
              <Button
                className={`blue-btn ${Math.ceil(paginationData.total_records / 20) === 1 ||
                  jumpPage === '' ||
                  Number(paginationData.page) === Number(jumpPage)
                  ? 'disabled'
                  : ''
                  } px-3`}
                clickHandler={() => {
                  handleJumpToPage();
                  setJumpPage('');
                }}
                disabled={
                  Math.ceil(paginationData.total_records / 20) === 1 ||
                  jumpPage === '' ||
                  Number(paginationData.page) === Number(jumpPage)
                }>
                Go
              </Button>
            </div>

            <Button
              className={`blue-btn d-flex align-items-center justify-content-center px-3 me-2 mx-1 ${paginationData.page === Math.ceil(paginationData.total_records / 20)
                ? 'disabled'
                : ''
                }`}
              clickHandler={() =>
                handlePageChange((prev) =>
                  Math.min(prev + 1, Math.ceil(paginationData.total_records / 20)),
                )
              }
              disabled={paginationData.page === Math.ceil(paginationData.total_records / 20)}>
              Next
            </Button>
          </div>
        </div>
      )}
      {!listView && (
        <div className="bg-white py-4 px-3 matrix-box mfg-tile-view">
          {isLoading ? (
            <div className="text-center my-2">
              <span className="spinner-border spinner-border-sm text-primary" role="status" />
              <span className="ms-2">Loading...</span>
            </div>
          ) : Array.isArray(sortedData) && sortedData.length > 0 ? (
            <div className="row custom-row">
              {sortedData.map((lot) =>
                lot.ready_qty === 0 ? (
                  <RedTile key={lot.id} lot={lot} />
                ) : lot.ready_qty < lot.total_qty ? (
                  <YellowTile key={lot.id} lot={lot} />
                ) : (
                  <GreenTile key={lot.id} lot={lot} />
                )
              )}
            </div>
          ) : (
            <div>
              <h4 className="px-1 text-center py-5">No Records Found</h4>
            </div>
          )}
        </div>
      )}
      {listView && (
        <PaginatedTable
          columns={MANUFACTURING_ESUN_LIST_COLUMN}
          initialData={sortedData}
          colFilter={false}
          to={ROUTES.MANUFACTURING}
          pageNumber={pageNumber}
          setPageNumber={handlePageChange}
          paginationData={paginationData}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export const getReadyQty = (lot) => {
  const qty = lot?.batch?.reduce((acc, cum) => {
    cum.state === DATA_FIELD.PACKED ? (acc = acc + cum.order_qty) : (acc = acc + 0);
    return acc;
  }, 0);
  return qty;
};

export const getBatchQty = (lot) => {
  const qty = reducedCalc(lot?.batch, DATA_FIELD.ORDER_QTY);
  return qty;
};
